import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { getFcmToken } from "../../lib/fcm";
import { toast } from "sonner";
import { BellRing, BellOff, Loader2, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * Push notification diagnostics for the tenant admin.
 *
 * Shows whether THIS device is registered with the backend for native
 * FCM/APNs delivery, and provides a one-click "Re-register" button that
 * bypasses every local cache and forces a fresh handshake. This is the
 * recovery path when the auto-registration on auth-refresh silently
 * failed (cold network, Firebase not ready, etc.) and the owner needs
 * to fix it without uninstalling the app.
 */
function isCapacitorNative() {
  const cap = typeof window !== "undefined" ? window.Capacitor : null;
  return !!(cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform());
}

export default function PushDiagnostics() {
  const native = isCapacitorNative();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users/me/push-status");
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const forceRegister = async () => {
    setBusy(true);
    try {
      // Clear any cached token so getFcmToken re-runs the full handshake.
      try { localStorage.removeItem("kb_fcm_token"); } catch { /* sandbox */ }
      const token = await getFcmToken();
      if (token) {
        toast.success("Notifications enabled on this device", {
          description: `Token registered (${token.slice(0, 12)}…). Try sending a test from Super Admin.`,
        });
      } else {
        toast.error("Could not register device", {
          description: native
            ? "Permission was granted but no token was produced. Restart the app and try again — if it persists, super-admin needs to verify the APNs Authentication Key is uploaded to Firebase Console."
            : "Web Push is unavailable in this browser. Install the native app for reliable push delivery.",
          duration: 12000,
        });
      }
      await loadStatus();
    } catch (e) {
      // Surface the REAL error from Capacitor — Apple's exact wording
      // is the fastest path to the right fix. Common iOS errors:
      //
      //   • "no valid 'aps-environment' entitlement string found for application"
      //       → Push Notifications capability missing in Xcode signing.
      //   • "remote notifications are not supported in the simulator"
      //       → Build to a real device or TestFlight.
      //   • "SENDER_ID_MISMATCH" / "TOO_MANY_REGISTRATIONS"
       //      → google-services config baked into APK doesn't match Firebase project.
      //   • iOS hangs ~30s → registration timed out:
      //       → MOST LIKELY: APNs Auth Key (.p8) not uploaded to Firebase
      //         Console → Project Settings → Cloud Messaging → iOS app config.
      const raw = e?.message || String(e || "Unknown error");
      let hint = "";
      const lower = raw.toLowerCase();
      if (lower.includes("aps-environment") || lower.includes("entitlement")) {
        hint = " — Push Notifications capability missing in Xcode. Add it under Signing & Capabilities.";
      } else if (lower.includes("simulator")) {
        hint = " — iOS Simulator can't register for push. Use a real device.";
      } else if (lower.includes("timed out") || lower.includes("timeout")) {
        hint = native
          ? " — Common causes: (1) Firebase iOS SDK not added to Xcode project as Swift Package, (2) AppDelegate.swift missing `FirebaseApp.configure()` + APNs→FCM bridge, (3) APNs Auth Key (.p8) not uploaded to Firebase Console. Rebuild with the latest scripts/build_tenant_app.py."
          : "";
      } else if (lower.includes("sender_id") || lower.includes("token_invalid")) {
        hint = " — google-services config in the build doesn't match the Firebase project for this tenant.";
      } else if (native && (lower === "registrationerror" || lower.includes("no detail") || lower === "{}" || lower.length < 30)) {
        // iOS gave an empty/generic error. By far the #1 cause at this
        // stage (everything else verified) is a PROVISIONING PROFILE
        // mismatch — the profile baked into the IPA doesn't include the
        // Push Notifications entitlement. iOS rejects the registration
        // attempt before any APNs traffic happens.
        hint = " — Most likely a provisioning profile issue. Fix in Xcode: " +
               "1) Signing & Capabilities → make sure 'Automatically manage signing' is ON. " +
               "2) Confirm Push Notifications capability is listed on the App target. " +
               "3) Apple Developer Console → Certificates, IDs & Profiles → Identifiers → your App ID → enable Push Notifications service if not already. " +
               "4) Clean Build Folder (Shift+Cmd+K), Archive again, upload fresh build.";
      }
      toast.error("Registration failed", {
        description: raw + hint,
        duration: 15000,
      });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="border border-[hsl(var(--kb-border))] bg-white p-4 flex items-center gap-2 text-sm text-[hsl(var(--kb-muted))]">
        <Loader2 size={14} className="animate-spin" /> Checking notification setup…
      </div>
    );
  }

  const subscribed = status?.has_subscription;
  return (
    <div
      className={`border p-4 flex items-start gap-3 ${subscribed ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"}`}
      data-testid="push-diagnostics-card">
      <div className="mt-0.5">
        {subscribed
          ? <CheckCircle2 size={20} className="text-green-700" />
          : <AlertCircle size={20} className="text-amber-700" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm flex items-center gap-2">
          {subscribed ? <BellRing size={14} /> : <BellOff size={14} />}
          {subscribed
            ? `Notifications enabled · ${status.subscription_count} device${status.subscription_count === 1 ? "" : "s"} registered`
            : "Notifications NOT registered on this device"}
        </div>
        <div className="text-xs text-[hsl(var(--kb-muted))] mt-1">
          {subscribed
            ? "You'll receive order alerts on this device. If you stop getting them, click Re-register."
            : native
              ? "Tap Re-register below to enable order notifications. This device will then receive a push the moment a customer places an order."
              : "Web browser detected. For reliable order alerts, install the native app and re-register from inside it."}
        </div>
      </div>
      <button
        onClick={forceRegister}
        disabled={busy}
        className="kb-btn-outline text-xs inline-flex items-center gap-1.5 shrink-0"
        data-testid="push-diagnostics-reregister-btn">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
        {subscribed ? "Re-register" : "Enable"}
      </button>
    </div>
  );
}
